/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  useState,
  useMemo,
  useContext,
  createContext,
  useRef,
  useEffect,
  ChangeEventHandler,
  PropsWithChildren,
  FC,
} from 'react'
import { reaction } from 'mobx'
import '@blueprintjs/core/lib/css/blueprint.css'
import { getImageSize } from 'polotno/utils/image'
import { createPolotnoStore } from './createPolotnoStore'
import useStableCallback from '../../../hooks/shared/useStableCallback'
import { useStateWithRef } from '../../../hooks/shared/useStateWithRef'
import { TextElementType } from 'polotno/model/text-model'
import { ImageElementType } from 'polotno/model/image-model'
import useCallbackDebouncedAsync from '../../../hooks/shared/useCallbackDebouncedAsync'
import { PageType } from 'polotno/model/page-model'
import { ElementType } from 'polotno/model/group-model'
import config from '../../../config'

export type ValidationError = {
  element: ElementType
  message: string
  tooltip: string
}

export type PdfEditorProps = {
  templateJSON?: any
  saving: boolean
  onSave: (pdf: Blob, templateJSON: Blob) => Promise<void>
  onClose: (hasChanges: boolean) => void
}

const PdfEditorContext = createContext<{
  handleFilePickerChange: ChangeEventHandler<HTMLInputElement>
  insertMergeField: (mergeField: string) => void
  saving: boolean
  onSave: (pdf: Blob, templateJSON: Blob) => Promise<void>
  onClose: (hasChanges: boolean) => void
  store: ReturnType<typeof createPolotnoStore>
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  setPreparingSave: React.Dispatch<React.SetStateAction<boolean>>
  updated: boolean
  setUpdated: React.Dispatch<React.SetStateAction<boolean>>
  updatedRef: React.MutableRefObject<boolean>
  contextToolbarVisible: boolean
  setContextToolbarVisible: React.Dispatch<React.SetStateAction<boolean>>
  selectedTextElement: TextElementType | undefined
  setSelectedTextElement: React.Dispatch<React.SetStateAction<TextElementType | undefined>>
  selectedImageElement: ImageElementType | undefined
  setSelectedImageElement: React.Dispatch<React.SetStateAction<ImageElementType | undefined>>
  validating: boolean
  setValidating: React.Dispatch<React.SetStateAction<boolean>>
  validationErrors: ValidationError[]
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationError[]>>
  filePickerRef: React.RefObject<HTMLInputElement>
  activeTextArea: HTMLTextAreaElement | undefined
  setActiveTextArea: React.Dispatch<React.SetStateAction<HTMLTextAreaElement | undefined>>
} | null>(null)

const usePdfEditor = () => {
  const context = useContext(PdfEditorContext)
  if (!context) {
    throw new Error('no PdfEditorProvider found when calling usePdfEditor()')
  }
  return context
}

export class PolotnoWindowInstance {
  private store: ReturnType<typeof createPolotnoStore>
  constructor({ store }: { store: ReturnType<typeof createPolotnoStore> }) {
    this.store = store
  }

  getElementsByType = (type: 'text' | 'line' | 'figure' | 'image' | 'svg') => {
    const elements: ElementType[] = []
    this.store.find(element => {
      const isPage = this.store.pages.includes(element as PageType)
      if (isPage) {
        return false
      }
      if (element.type === type) {
        elements.push(element as ElementType)
      }
      return false
    })
    return elements
  }

  getTextElementByText = ({ text }: { text: string }) => {
    const textElements = this.getElementsByType('text') as TextElementType[]
    const elementsWithText = textElements.filter(element => element.text === text)
    if (elementsWithText.length > 1) {
      throw new Error('Multiple elements with given text on the page')
    }
    return elementsWithText[0]
  }
  moveElement = ({ element, x, y }: { element: ElementType; x: number; y: number }) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    element.set({ x, y })
  }
  createTextElement = ({ text, x = 50, y = 50 }: { text: string; x: number; y: number }) => {
    const element = this.store.activePage.addElement({
      type: 'text',
      x,
      y,
      fill: 'black',
      text,
      align: 'left',
      placeholder: 'Please enter text here',
    })
    return element
  }
}

const PdfEditorProvider: FC<PropsWithChildren & PdfEditorProps> = ({
  children,
  templateJSON,
  saving,
  onSave,
  onClose,
}) => {
  const store = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return createPolotnoStore({ templateJSON })
  }, [templateJSON])
  const [preparingSave, setPreparingSave] = useState(false)
  const [loading, setLoading] = useState(true)
  const [updated, setUpdated, updatedRef] = useStateWithRef(false)
  const [contextToolbarVisible, setContextToolbarVisible, contextToolbarVisibleRef] =
    useStateWithRef(false)
  const [selectedTextElement, setSelectedTextElement, selectedTextElementRef] =
    useStateWithRef<TextElementType>()
  const [selectedImageElement, setSelectedImageElement, selectedImageElementRef] =
    useStateWithRef<ImageElementType>()
  const filePickerRef = useRef<HTMLInputElement>(null)
  const [validating, setValidating, validatingRef] = useStateWithRef(false)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const [activeTextArea, setActiveTextArea] = useState<HTMLTextAreaElement>()
  useEffect(() => {
    store.waitLoading().then(() => {
      setLoading(false)
    })
  }, [store])
  const windowInstance = useRef(new PolotnoWindowInstance({ store }))
  useEffect(() => {
    if (!loading && config.local) {
      // @ts-expect-error polotnoInstance is needed for e2e
      window.polotnoInstance = windowInstance.current
    }
    return () => {
      // @ts-expect-error polotnoInstance is needed for e2e
      if (window.polotnoInstance) {
        // @ts-expect-error polotnoInstance is needed for e2e
        window.polotnoInstance = undefined
      }
    }
  }, [loading])
  const getValidationIssues = useCallbackDebouncedAsync(
    async () => {
      const validationErrors: ValidationError[] = []
      const elements: ElementType[] = []
      // Recursively find all elements in the store, including those in groups
      // Skip pages themselves as they're not elements we want to validate
      store.find(item => {
        const isPage = store.pages.includes(item as PageType)
        if (isPage) {
          return false
        }
        elements.push(item as ElementType)
        return false
      })
      for (const element of elements) {
        // // --- Check 1: Element Position ---
        // // Get element's bounding box (accounts for rotation)
        // const { x, y, width, height } = getClientRect({
        //   height: element.height as number,
        //   width: element.width as number,
        //   rotation: element.rotation as number,
        //   x: element.x as number,
        //   y: element.y as number,
        // })
        // // Verify element is within page boundaries
        // if (
        //   x < 0 ||
        //   y < 0 ||
        //   x + width > (store.activePage.width as number) ||
        //   y + height > (store.activePage.height as number)
        // ) {
        //   validationErrors.push({
        //     element,
        //     message: 'Element is outside page bounds',
        //   })
        // }

        // // --- Check 2: Text Size ---
        // // Ensure text is readable by checking minimum font size
        // if (element.type === 'text' && element.fontSize < 10) {
        //   validationErrors.push({
        //     element,
        //     message: 'Text size is too small',
        //     tooltip: 'Small text is not recommended'
        //   })
        // }

        // --- Check 3: Image Resolution ---
        // Verify image quality by comparing original vs displayed dimensions
        if (element.type === 'image') {
          // TODO: probably we should cache image size per src somewhere
          // to avoid calling getImageSize on every element change
          const { width, height } = (await getImageSize(element.src).catch(error => ({
            width: Math.max,
            height: Math.max,
          }))) as {
            width: number
            height: number
          }
          // Calculate actual displayed size after cropping
          const croppedWidth = width * element.cropWidth
          const croppedHeight = height * element.cropHeight
          // Check if image is being stretched beyond its original resolution
          if (croppedWidth < element.width || croppedHeight < element.height) {
            validationErrors.push({
              element,
              message: 'Low image quality',
              tooltip:
                'This means you have stretched the image to be larger than its original size. It is likely to look pixelated when printed.',
            })
          }
        }
      }
      return { validationErrors }
    },
    [store],
    800,
  )
  const validateElements = useStableCallback(() => {
    setValidating(true)
    getValidationIssues().then(({ validationErrors }) => {
      if (validatingRef.current) {
        setValidationErrors(validationErrors)
        setValidating(false)
      }
    })
  })
  useEffect(() => {
    const unsubscribe = store.on('change', async () => {
      if (!updatedRef.current) {
        setUpdated(true)
      }
      validateElements()
    })
    return unsubscribe
  }, [updatedRef, setUpdated, validateElements, store])
  useEffect(() => {
    // use mobx reaction to react to selection changes
    const unsubscribe = reaction(
      () => {
        console.info(`>>> reaction`)
        if (store.selectedElements.length !== 1) {
          return {
            toolbarElementSelected: false,
            selectedImage: undefined,
            selectedText: undefined,
          }
        }
        const type = store.selectedElements[0]?.type
        const toolbarElementSelected =
          type === 'text' ||
          type === 'line' ||
          type === 'figure' ||
          type === 'image' ||
          type === 'svg'
        const imageSelected = type === 'image' || type === 'svg'
        const textSelected = type === 'text'
        return {
          toolbarElementSelected,
          selectedImage: imageSelected
            ? (store.selectedElements[0] as ImageElementType)
            : undefined,
          selectedText: textSelected ? (store.selectedElements[0] as TextElementType) : undefined,
        }
      },
      // we can use result returned from reaction
      ({ toolbarElementSelected, selectedImage, selectedText }) => {
        setContextToolbarVisible(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          toolbarElementSelected && (!selectedImage || !selectedImage?.custom.background),
        )
        setSelectedImageElement(selectedImage)
        setSelectedTextElement(selectedText)
      },
    )
    return unsubscribe
  }, [setContextToolbarVisible, setSelectedImageElement, setSelectedTextElement, store])
  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement
      if (target.tagName.toLowerCase() === 'textarea' && contextToolbarVisibleRef.current) {
        setActiveTextArea(event.target as HTMLTextAreaElement)
      } else {
        setActiveTextArea(undefined)
      }
    }
    document.addEventListener('focusin', handleFocusIn)
    return () => {
      document.removeEventListener('focusin', handleFocusIn)
    }
  }, [contextToolbarVisibleRef])
  const handleFilePickerChange: ChangeEventHandler<HTMLInputElement> = useStableCallback(event => {
    const file = event.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = event => {
        if (event.target?.result) {
          const img = new Image()
          img.onload = () => {
            if (selectedImageElementRef.current) {
              const selectedImageElement = store.selectedElements[0]
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call
              selectedImageElement.set({
                // @ts-expect-error unsafe props
                src: event.target.result as string,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                custom: {
                  ...selectedImageElement.custom,
                  naturalWidth: img.naturalWidth,
                  naturalHeight: img.naturalHeight,
                },
              })
            } else {
              const imageIsBiggerThanCanvas =
                img.naturalWidth > store.activePage.computedWidth ||
                img.naturalHeight > store.activePage.computedHeight
              store.activePage?.addElement({
                type: 'image',
                width: imageIsBiggerThanCanvas
                  ? store.activePage.computedWidth * 0.5
                  : img.naturalWidth,
                height: imageIsBiggerThanCanvas
                  ? store.activePage.computedHeight * 0.5
                  : img.naturalHeight,
                x: 50,
                y: 50,
                rotation: 0,
                // keepRatio: true,
                // @ts-expect-error event target is set
                src: event.target.result as string,
                custom: {
                  background: false,
                  naturalWidth: img.naturalWidth,
                  naturalHeight: img.naturalHeight,
                },
              })
            }
          }
          img.src = event.target.result as string
        }
      }
      reader.readAsDataURL(file)
    }
    event.target.value = ''
  })
  const insertMergeField = useStableCallback((mergeField: string) => {
    if (!activeTextArea) {
      return
    }
    const { selectionStart, selectionEnd, value } = activeTextArea
    const newValue = value.slice(0, selectionStart) + mergeField + value.slice(selectionEnd)
    // eslint-disable-next-line react-hooks/immutability
    activeTextArea.value = newValue
    const newCursorPos = selectionStart + mergeField.length
    activeTextArea.selectionStart = newCursorPos
    activeTextArea.selectionEnd = newCursorPos
    if (store.selectedElements[0].type === 'text') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      store.selectedElements[0].set({ text: newValue })
    }
  })
  const value = useMemo(
    () => ({
      handleFilePickerChange,
      insertMergeField,
      setPreparingSave,
      saving: saving || preparingSave,
      onSave,
      onClose,
      store,
      loading,
      setLoading,
      updated,
      setUpdated,
      updatedRef,
      contextToolbarVisible,
      setContextToolbarVisible,
      selectedTextElement,
      setSelectedTextElement,
      selectedImageElement,
      setSelectedImageElement,
      validating,
      setValidating,
      validationErrors,
      setValidationErrors,
      filePickerRef,
      activeTextArea,
      setActiveTextArea,
    }),
    [
      preparingSave,
      setPreparingSave,
      handleFilePickerChange,
      insertMergeField,
      saving,
      onSave,
      onClose,
      store,
      loading,
      setLoading,
      updated,
      setUpdated,
      updatedRef,
      contextToolbarVisible,
      setContextToolbarVisible,
      selectedTextElement,
      setSelectedTextElement,
      selectedImageElement,
      setSelectedImageElement,
      validating,
      setValidating,
      validationErrors,
      setValidationErrors,
      filePickerRef,
      activeTextArea,
      setActiveTextArea,
    ],
  )
  return <PdfEditorContext.Provider value={value}>{children}</PdfEditorContext.Provider>
}

export { usePdfEditor, PdfEditorProvider }
export default PdfEditorProvider
